import { useState, useEffect } from "react";
import { 
  FileText, GripVertical, Download, Loader2, Check, 
  ExternalLink, Save, BookOpen, ImageIcon, AlertTriangle, Eye 
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { SmartDocumentViewer } from "@/components/viewer/SmartDocumentViewer";
import { ExpirationAlert, ExpirationBadge, getExpiredDocuments } from "./ExpirationAlert";
import { CoverPagePreview } from "./CoverPagePreview";

interface LinkedDocument {
  id: string;
  label: string;
  doc_type: string | null;
  document_id: string;
  sort_order: number;
  document: {
    id: string;
    title: string;
    file_name: string;
    file_path: string;
    file_size: number | null;
    mime_type: string;
    expires_at?: string | null;
  };
}

interface MergePDFDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  demarcheTitle: string;
  linkedDocuments: LinkedDocument[];
}

interface MergedResult {
  blob: Blob;
  url: string;
  fileName: string;
}

export function MergePDFDialog({
  open,
  onOpenChange,
  demarcheTitle,
  linkedDocuments,
}: MergePDFDialogProps) {
  const [orderedDocs, setOrderedDocs] = useState<LinkedDocument[]>([]);
  const [includeCoverPage, setIncludeCoverPage] = useState(true);
  const [saveToDocuments, setSaveToDocuments] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [mergedResult, setMergedResult] = useState<MergedResult | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [showExpirationWarning, setShowExpirationWarning] = useState(false);

  // Check for expired documents
  const expiredDocs = getExpiredDocuments(orderedDocs.length > 0 ? orderedDocs : linkedDocuments);
  const hasExpiredDocs = expiredDocs.length > 0;

  // Initialize ordered docs when dialog opens
  useEffect(() => {
    if (open && linkedDocuments.length > 0) {
      setOrderedDocs([...linkedDocuments].sort((a, b) => a.sort_order - b.sort_order));
    }
  }, [open, linkedDocuments]);

  // Reset state when dialog opens
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setOrderedDocs([...linkedDocuments].sort((a, b) => a.sort_order - b.sort_order));
      setMergedResult(null);
    } else {
      if (mergedResult?.url) {
        URL.revokeObjectURL(mergedResult.url);
      }
      setMergedResult(null);
      setOrderedDocs([]);
    }
    onOpenChange(newOpen);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newDocs = [...orderedDocs];
    const [draggedItem] = newDocs.splice(draggedIndex, 1);
    newDocs.splice(index, 0, draggedItem);
    setOrderedDocs(newDocs);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Generate cover page
  const generateCoverPage = async (pdfDoc: PDFDocument): Promise<void> => {
    const page = pdfDoc.insertPage(0, [595.28, 841.89]); // A4 size
    const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
    
    const { width, height } = page.getSize();
    const margin = 50;
    let y = height - margin;

    // Header line
    page.drawLine({
      start: { x: margin, y: y },
      end: { x: width - margin, y: y },
      thickness: 2,
      color: rgb(0.2, 0.4, 0.8),
    });
    y -= 40;

    // Title
    page.drawText('DOSSIER', {
      x: margin,
      y,
      size: 14,
      font: helvetica,
      color: rgb(0.5, 0.5, 0.5),
    });
    y -= 30;

    // Demarche title (wrap if too long)
    const titleLines = wrapText(demarcheTitle, 50);
    for (const line of titleLines) {
      page.drawText(line, {
        x: margin,
        y,
        size: 24,
        font: helveticaBold,
        color: rgb(0.1, 0.1, 0.1),
      });
      y -= 35;
    }
    y -= 20;

    // Date
    const dateStr = new Date().toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    page.drawText(`Généré le ${dateStr}`, {
      x: margin,
      y,
      size: 11,
      font: helvetica,
      color: rgb(0.5, 0.5, 0.5),
    });
    y -= 50;

    // Separator
    page.drawLine({
      start: { x: margin, y: y },
      end: { x: width - margin, y: y },
      thickness: 0.5,
      color: rgb(0.8, 0.8, 0.8),
    });
    y -= 40;

    // Documents list title
    page.drawText('DOCUMENTS INCLUS', {
      x: margin,
      y,
      size: 12,
      font: helveticaBold,
      color: rgb(0.3, 0.3, 0.3),
    });
    y -= 30;

    // List documents
    orderedDocs.forEach((doc, index) => {
      const number = String(index + 1).padStart(2, '0');
      page.drawText(`${number}.`, {
        x: margin,
        y,
        size: 11,
        font: helveticaBold,
        color: rgb(0.2, 0.4, 0.8),
      });
      page.drawText(doc.label, {
        x: margin + 25,
        y,
        size: 11,
        font: helvetica,
        color: rgb(0.2, 0.2, 0.2),
      });
      y -= 22;
    });

    // Footer
    y = margin + 20;
    page.drawLine({
      start: { x: margin, y: y },
      end: { x: width - margin, y: y },
      thickness: 0.5,
      color: rgb(0.8, 0.8, 0.8),
    });
    y -= 15;
    page.drawText(`${orderedDocs.length} document${orderedDocs.length > 1 ? 's' : ''} • Généré par EasyFinder`, {
      x: margin,
      y,
      size: 9,
      font: helvetica,
      color: rgb(0.6, 0.6, 0.6),
    });
  };

  // Helper to wrap text
  function wrapText(text: string, maxChars: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      if ((currentLine + ' ' + word).trim().length <= maxChars) {
        currentLine = (currentLine + ' ' + word).trim();
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) lines.push(currentLine);
    return lines;
  }

  // Convert image to PDF page
  const convertImageToPdf = async (imageBlob: Blob, mimeType: string): Promise<Uint8Array> => {
    const pdfDoc = await PDFDocument.create();
    const imageBytes = await imageBlob.arrayBuffer();
    
    let image;
    if (mimeType === 'image/png') {
      image = await pdfDoc.embedPng(imageBytes);
    } else if (mimeType === 'image/jpeg' || mimeType === 'image/jpg') {
      image = await pdfDoc.embedJpg(imageBytes);
    } else {
      // Try as JPEG by default
      image = await pdfDoc.embedJpg(imageBytes);
    }

    // A4 dimensions
    const pageWidth = 595.28;
    const pageHeight = 841.89;
    const margin = 40;

    // Scale image to fit
    const maxWidth = pageWidth - (margin * 2);
    const maxHeight = pageHeight - (margin * 2);
    const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
    const scaledWidth = image.width * scale;
    const scaledHeight = image.height * scale;

    const page = pdfDoc.addPage([pageWidth, pageHeight]);
    page.drawImage(image, {
      x: (pageWidth - scaledWidth) / 2,
      y: (pageHeight - scaledHeight) / 2,
      width: scaledWidth,
      height: scaledHeight,
    });

    return pdfDoc.save();
  };

  // Handle merge button click - check for expired docs first
  const handleMergeClick = () => {
    if (hasExpiredDocs) {
      setShowExpirationWarning(true);
    } else {
      performMerge();
    }
  };

  // Merge PDFs
  const performMerge = async () => {
    setShowExpirationWarning(false);
    
    if (orderedDocs.length === 0) {
      toast.error("Aucun document à fusionner");
      return;
    }

    setProcessing(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const mergedPdf = await PDFDocument.create();

      // Add cover page if enabled
      if (includeCoverPage) {
        await generateCoverPage(mergedPdf);
      }

      // Process each document
      for (const doc of orderedDocs) {
        const { data, error } = await supabase.storage
          .from('documents')
          .download(doc.document.file_path);

        if (error || !data) {
          toast.error(`Impossible de télécharger: ${doc.label}`);
          continue;
        }

        let pdfBytes: ArrayBuffer;
        const mimeType = doc.document.mime_type;

        // Handle images by converting to PDF
        if (mimeType.startsWith('image/')) {
          const convertedBytes = await convertImageToPdf(data, mimeType);
          pdfBytes = convertedBytes.buffer as ArrayBuffer;
        } else if (mimeType === 'application/pdf') {
          pdfBytes = await data.arrayBuffer();
        } else {
          toast.error(`Format non supporté: ${doc.label}`);
          continue;
        }

        // Load and copy pages
        const srcDoc = await PDFDocument.load(pdfBytes);
        const pages = await mergedPdf.copyPages(srcDoc, srcDoc.getPageIndices());
        pages.forEach(page => mergedPdf.addPage(page));
      }

      const mergedBytes = await mergedPdf.save();
      const bytes = mergedBytes instanceof Uint8Array ? mergedBytes : new Uint8Array(mergedBytes);
      const copy = new Uint8Array(bytes.byteLength);
      copy.set(bytes);
      const blob = new Blob([copy.buffer as ArrayBuffer], { type: 'application/pdf' });
      
      // Validate
      if (blob.size === 0) {
        throw new Error("PDF généré vide");
      }

      const fileName = `dossier-${slugify(demarcheTitle)}-${Date.now()}.pdf`;

      // Save to documents if option is checked
      if (saveToDocuments) {
        const filePath = `${user.id}/${crypto.randomUUID()}.pdf`;
        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(filePath, blob);

        if (uploadError) throw uploadError;

        await supabase.from('documents').insert([{
          user_id: user.id,
          title: `Dossier: ${demarcheTitle}`,
          file_name: fileName,
          file_path: filePath,
          file_size: blob.size,
          mime_type: 'application/pdf',
          source: 'upload',
          status: 'complete',
          doc_type: 'other'
        }]);
      }

      // Store result
      const resultUrl = URL.createObjectURL(blob);
      setMergedResult({
        blob,
        url: resultUrl,
        fileName,
      });

      toast.success(`PDF fusionné (${mergedPdf.getPageCount()} pages)`);
    } catch (error: any) {
      console.error('Merge error:', error);
      toast.error(error.message || "Erreur lors de la fusion");
    } finally {
      setProcessing(false);
    }
  };

  // Helper to slugify
  function slugify(text: string): string {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase()
      .substring(0, 50);
  }

  const handleDownload = () => {
    if (!mergedResult) return;
    const a = document.createElement('a');
    a.href = mergedResult.url;
    a.download = mergedResult.fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("PDF téléchargé");
  };

  const handleOpenNewTab = () => {
    if (!mergedResult) return;
    window.open(mergedResult.url, '_blank');
  };

  const isImage = (mimeType: string) => mimeType.startsWith('image/');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Créer un PDF unique
          </DialogTitle>
          <DialogDescription>
            Fusionnez tous les documents en un seul PDF pour un dépôt physique ou numérique
          </DialogDescription>
        </DialogHeader>

        {!mergedResult ? (
          showExpirationWarning ? (
            <div className="space-y-4 py-4">
              <ExpirationAlert
                expiredDocs={expiredDocs}
                onContinue={performMerge}
                onCancel={() => setShowExpirationWarning(false)}
                loading={processing}
              />
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowExpirationWarning(false)} 
                  className="flex-1"
                  disabled={processing}
                >
                  Annuler
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={performMerge} 
                  className="flex-1"
                  disabled={processing}
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Fusionner quand même"
                  )}
                </Button>
              </div>
            </div>
          ) : (
          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            {/* Expired documents warning banner */}
            {hasExpiredDocs && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
                <p className="text-sm text-destructive">
                  <strong>{expiredDocs.length}</strong> document{expiredDocs.length > 1 ? 's' : ''} expiré{expiredDocs.length > 1 ? 's' : ''} dans ce dossier
                </p>
              </div>
            )}

            {/* Options */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="coverPage"
                  checked={includeCoverPage}
                  onCheckedChange={(checked) => setIncludeCoverPage(checked === true)}
                />
                <Label htmlFor="coverPage" className="flex items-center gap-1.5 cursor-pointer text-sm">
                  <BookOpen className="w-4 h-4" />
                  Page de garde
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="saveDocs"
                  checked={saveToDocuments}
                  onCheckedChange={(checked) => setSaveToDocuments(checked === true)}
                />
                <Label htmlFor="saveDocs" className="flex items-center gap-1.5 cursor-pointer text-sm">
                  <Save className="w-4 h-4" />
                  Sauvegarder dans Mes Documents
                </Label>
              </div>
            </div>

            {/* Cover page preview + Document list layout */}
            <div className="flex-1 overflow-hidden flex flex-col sm:flex-row gap-4">
              {/* Cover page preview - shown when option is checked */}
              {includeCoverPage && orderedDocs.length > 0 && (
                <div className="sm:w-48 shrink-0">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
                    <Eye className="w-3.5 h-3.5" />
                    <span>Page de garde</span>
                  </div>
                  <CoverPagePreview
                    title={demarcheTitle}
                    documents={orderedDocs.map(d => ({ id: d.id, label: d.label }))}
                    className="w-full"
                  />
                </div>
              )}

              {/* Document list with drag & drop */}
              <div className="flex-1 min-w-0 overflow-hidden">
                <p className="text-sm text-muted-foreground mb-2">
                  Glissez pour réorganiser l'ordre des documents :
              </p>
              <ScrollArea className="h-[280px] pr-4">
                <div className="space-y-2">
                  {orderedDocs.map((doc, index) => {
                    const isExpired = doc.document.expires_at && 
                      new Date(doc.document.expires_at) < new Date();
                    
                    return (
                    <div
                      key={doc.id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-grab active:cursor-grabbing transition-all ${
                        draggedIndex === index 
                          ? 'opacity-50 scale-95' 
                          : isExpired 
                            ? 'bg-destructive/5 border-destructive/30 hover:bg-destructive/10' 
                            : 'bg-card hover:bg-muted/50'
                      }`}
                    >
                      <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                      <Badge variant="outline" className="shrink-0 text-xs font-mono">
                        {String(index + 1).padStart(2, '0')}
                      </Badge>
                      {isExpired ? (
                        <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
                      ) : isImage(doc.document.mime_type) ? (
                        <ImageIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                      ) : (
                        <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-medium truncate ${isExpired ? 'text-destructive' : ''}`}>
                            {doc.label}
                          </p>
                          <ExpirationBadge expiresAt={doc.document.expires_at} compact />
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-muted-foreground truncate">
                            {doc.document.file_name}
                          </p>
                          {isImage(doc.document.mime_type) && (
                            <Badge variant="secondary" className="text-[10px]">
                              Image → PDF
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
              </ScrollArea>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleMergeClick}
                disabled={processing || orderedDocs.length === 0}
                className="flex-1"
                variant={hasExpiredDocs ? "destructive" : "default"}
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Fusion en cours...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Créer le PDF
                  </>
                )}
              </Button>
            </div>
          </div>
          )
        ) : (
          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            {/* Success header */}
            <div className="flex items-center gap-2 text-primary">
              <Check className="w-5 h-5" />
              <span className="font-medium">PDF créé avec succès</span>
            </div>

            {/* Preview */}
            <div className="flex-1 min-h-0 border rounded-lg overflow-hidden bg-muted/30">
              <SmartDocumentViewer
                url={mergedResult.url}
                fileData={mergedResult.blob}
                title={mergedResult.fileName}
                mimeType="application/pdf"
                showControls={true}
              />
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={handleDownload}
                className="flex-1"
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger
              </Button>
              <Button
                variant="outline"
                onClick={handleOpenNewTab}
                className="flex-1"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Ouvrir dans un onglet
              </Button>
              <Button
                onClick={() => handleOpenChange(false)}
                className="flex-1"
              >
                Fermer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
